// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import logger from "@insite/client-framework/Logger";
import debounce from "lodash/debounce";
import { useEffect, useMemo, useRef } from "react";

interface Props {
    multipleSaleQty?: number;
    minQtyState?: number;
    onQtyOrderedChanged?: (num: number) => void;
    setProductQty?: (value: string) => void;
    productId?: string;
}

const xUseQty = ({
    multipleSaleQty = 1,
    minQtyState = 1,
    onQtyOrderedChanged,
    setProductQty = (value: string) => {},
    productId,
}: Props) => {
    const CallbackRefs = useRef<any>({});
    // Avoid Add those functions to dep. array
    CallbackRefs.current.onQtyOrderedChanged = onQtyOrderedChanged;
    CallbackRefs.current.setProductQty = setProductQty;
    const handleChange = (value: string, callback?: () => void) => {
        let quantityValue = Number(value === "" || value === "0" ? "1" : value);
        if (multipleSaleQty && multipleSaleQty > 1 && quantityValue % multipleSaleQty > 0) {
            quantityValue =
                quantityValue < multipleSaleQty
                    ? multipleSaleQty
                    : Math.ceil(quantityValue / multipleSaleQty) * multipleSaleQty;
        }
        if (quantityValue < minQtyState) {
            quantityValue = minQtyState;
        }

        CallbackRefs.current.setProductQty?.(quantityValue.toString());
        if (!CallbackRefs.current.onQtyOrderedChanged) {
            logger.warn(`There was no onQtyOrderedChanged passed to the ProductContext for ${productId}`);
            return;
        }
        if (!Number.isNaN(quantityValue)) {
            CallbackRefs.current.onQtyOrderedChanged?.(quantityValue);
            callback?.();
        }
    };
    const debouncedHandleChange = useMemo(() => debounce(handleChange, 500), [multipleSaleQty, minQtyState]);

    useEffect(() => {
        return () => {
            debouncedHandleChange.cancel();
        };
    }, [debouncedHandleChange]);

    return {
        debouncedHandleChange,
        handleChange,
    };
};

export default xUseQty;
